import React, { useEffect, useRef } from "react";
import { useIsDesktop } from "../utils/desktop";
import { useVolume } from "../components/VolumeContext";

const VIDEO_SRC = "https://www.youtube.com/embed/yd1nXpRnrfw?enablejsapi=1";
const YOUTUBE_ORIGIN = "https://www.youtube.com";

// Talks to the embed through the YouTube iframe API's postMessage protocol.
const post = (iframe, message) =>
  iframe?.contentWindow?.postMessage(JSON.stringify(message), YOUTUBE_ORIGIN);

const command = (iframe, func, args = []) => post(iframe, { event: "command", func, args });

const applyVolume = (iframe, { level, muted }) => {
  command(iframe, "setVolume", [Math.round(level * 100)]);
  command(iframe, muted ? "mute" : "unMute");
};

const VideoPlayer = () => {
  const iframeRef = useRef(null);
  const isDesktop = useIsDesktop();
  const { level, muted } = useVolume();
  const volumeRef = useRef({ level, muted });

  useEffect(() => {
    volumeRef.current = { level, muted };
    applyVolume(iframeRef.current, volumeRef.current);
  }, [level, muted]);

  // The embed ignores commands until its player is ready, so reapply once it reports in.
  useEffect(() => {
    const onMessage = (e) => {
      const iframe = iframeRef.current;
      if (e.origin !== YOUTUBE_ORIGIN || !iframe || e.source !== iframe.contentWindow) return;
      let data;
      try {
        data = JSON.parse(e.data);
      } catch {
        return;
      }
      if (data.event === "onReady" || data.event === "initialDelivery") {
        applyVolume(iframe, volumeRef.current);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Hidden windows stay mounted on mobile, so stop the video rather than let it play unseen.
  useEffect(() => {
    if (!isDesktop) command(iframeRef.current, "pauseVideo");
  }, [isDesktop]);

  return (
    <div className="h-full bg-black">
      <iframe
        ref={iframeRef}
        className="w-full h-full"
        src={VIDEO_SRC}
        title="Saylo demo"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        // Subscribes this page to the player's events, which is how it learns the player is ready.
        onLoad={() => post(iframeRef.current, { event: "listening" })}
      />
    </div>
  );
};

export default VideoPlayer;
